import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import AdminMobileMenuButton from 'components/admin/AdminMobileMenuButton';
import { DataTableTheme } from 'components/shared/DataTable';
import DataTable from 'components/shared/DataTable';
import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminFlagsQueryKeys, adminIndexFlags, FlagOptions } from 'services/api/admin/flags';
import { colors } from 'styles/theme/colors';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';

export default function FeatureFlags() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const canEdit = profile.has(PERMISSIONS.adminFeatureFlagEdit);

  const { data = [], isLoading } = useQuery({
    queryKey: adminFlagsQueryKeys.index(),
    queryFn: () => adminIndexFlags(),
  });

  const columnHelper = createColumnHelper<FlagOptions>();

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Feature Name',
        cell: (info) => <strong>{info.getValue()}</strong>,
      }),
      columnHelper.accessor('clients', {
        header: 'Clients',
        enableSorting: false,
        cell: (info) => {
          const names = [...new Set(info.getValue().map((c) => c.name))].join(', ');
          return <span>{names || '—'}</span>;
        },
      }),
      ...(canEdit
        ? [
            columnHelper.accessor((row) => row, {
              id: 'actions',
              header: '',
              enableSorting: false,
              size: 60,
              cell: (info) => (
                <ActionCell>
                  <Menu>
                    <Menu.Trigger>
                      <MenuTriggerButton>
                        <EllipsisIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
                      </MenuTriggerButton>
                    </Menu.Trigger>
                    <Menu.Content position='right' style={{ padding: 0, minWidth: 180 }}>
                      <Menu.Item
                        style={{ padding: '12px', fontSize: '14px' }}
                        onClick={() => navigate(`/admin/feature-flags/${info.row.original.name}/edit`)}>
                        <MenuIconWrapper>
                          <PencilIcon width={16} height={16} />
                        </MenuIconWrapper>
                        Edit
                      </Menu.Item>
                    </Menu.Content>
                  </Menu>
                </ActionCell>
              ),
            }),
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit]
  );

  return (
    <Container>
      <Header>
        <TitleRow>
          <AdminMobileMenuButton />
          <h1>Feature Flags</h1>
        </TitleRow>
      </Header>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          defaultSortBy={[{ id: 'title', desc: false }]}
          theme={DataTableTheme.OPEN}
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 24px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  h1 {
    font-size: 24px;
    font-weight: 500;
    margin: 0;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const MenuTriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: ${colors.black50};

  &:hover {
    background: ${colors.black5};
  }
`;

const MenuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  margin-right: 8px;
  color: ${colors.black50};
`;
